import React, { useState, useRef, useEffect } from 'react';

interface Option {
  value: string | number;
  label: string;
}

interface CustomDropdownProps {
  value: string | number;
  onChange: (value: any) => void;
  options: Option[];
  label?: string;
  /** 앞에 붙는 작은 그림. 예전엔 이모지 글자였다(`icon="📊"`) — 지금은 SVG 조각을 받는다 */
  icon?: React.ReactNode;
  style?: React.CSSProperties;
  compact?: boolean; // 작은 버전
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
  value,
  onChange,
  options,
  label,
  icon = '',
  style = {},
  compact = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 외부 클릭 감지
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div style={{ position: 'relative', zIndex: isOpen ? 10000 : 'auto', ...style }} ref={dropdownRef}>
      {label && (
        <label style={{
          fontSize: 'clamp(11px, 2.2vw, 13px)',
          fontWeight: 700,
          color: '#667eea',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          display: 'block',
          marginBottom: 'clamp(6px, 1.2vw, 8px)'
        }}>
          {icon} {label}
        </label>
      )}

      {/* 드롭다운 버튼 */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: compact ? '0 clamp(8px, 1.5vw, 10px)' : 'clamp(12px, 2.2vw, 14px) clamp(16px, 3vw, 20px)',
          height: compact ? 'clamp(32px, 6vh, 36px)' : 'auto',
          fontSize: compact ? 'clamp(10px, 2vw, 12px)' : 'clamp(13px, 2.5vw, 15px)',
          fontWeight: 600,
          border: compact ? '2px solid #667eea' : '3px solid #667eea',
          borderRadius: compact ? 'clamp(8px, 1.5vw, 10px)' : 'clamp(12px, 2.5vw, 16px)',
          background: isOpen
            ? 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
            : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#ffffff',
          cursor: 'pointer',
          outline: 'none',
          transition: 'all 0.3s ease',
          boxShadow: isOpen
            ? (compact ? '0 4px 12px rgba(102, 126, 234, 0.3)' : '0 8px 28px rgba(102, 126, 234, 0.45)')
            : (compact ? '0 2px 8px rgba(102, 126, 234, 0.2)' : '0 6px 20px rgba(102, 126, 234, 0.3)'),
          transform: isOpen ? (compact ? 'translateY(-1px)' : 'translateY(-3px) scale(1.02)') : 'translateY(0) scale(1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          textAlign: 'left'
        }}
      >
        <span>{selectedOption?.label || '선택'}</span>
        <span style={{
          fontSize: compact ? 'clamp(9px, 1.8vw, 10px)' : 'clamp(12px, 2.2vw, 14px)',
          transition: 'transform 0.3s ease',
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          display: 'inline-block'
        }}>▼</span>
      </button>

      {/* 드롭다운 박스 */}
      {isOpen && (
        <div style={{
          position: 'absolute',
          top: 'calc(100% + clamp(6px, 1.2vw, 8px))',
          left: 0,
          right: 0,
          background: '#ffffff',
          border: '3px solid #667eea',
          borderRadius: 'clamp(12px, 2.5vw, 16px)',
          boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
          zIndex: 10001,
          overflow: 'hidden',
          animation: 'slideDown 0.3s ease'
        }}>
          {options.map((option, index) => (
            <button
              key={option.value}
              type="button"
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                width: '100%',
                padding: 'clamp(12px, 2.2vw, 14px) clamp(16px, 3vw, 20px)',
                border: 'none',
                background: value === option.value
                  ? 'linear-gradient(135deg, #f0f3ff 0%, #e8edff 100%)'
                  : '#ffffff',
                color: value === option.value ? '#667eea' : '#333',
                fontSize: 'clamp(13px, 2.5vw, 15px)',
                fontWeight: value === option.value ? 700 : 500,
                textAlign: 'left',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                borderTop: index > 0 ? '1px solid rgba(102, 126, 234, 0.1)' : 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
              onMouseEnter={(e) => {
                if (value !== option.value) {
                  e.currentTarget.style.background = 'rgba(102, 126, 234, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                if (value !== option.value) {
                  e.currentTarget.style.background = '#ffffff';
                }
              }}
            >
              <span>{option.label}</span>
              {value === option.value && <span style={{ fontSize: 'clamp(16px, 3vw, 18px)' }}>✓</span>}
            </button>
          ))}
        </div>
      )}

      {/* 애니메이션 */}
      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default CustomDropdown;
